// Referral program — hybrid (activation bonus + ongoing % of referee spend in
// Pulsar Points × tier multiplier, capped at 12 months).
//
// Cohort-tracked: each month's new referral acquisitions form a cohort that
// generates ongoing share for the next 12 months. Forecast.js calls these in
// sequence per month: estimateNewReferrals → computeReferralCost.
//
// Defensive: if inputs.referral is undefined or .enabled === false, returns 0.

import { blendedCostPerPoint } from './cashback.js';

const FALLBACK_REFERRAL = {
  enabled: false,
  activationBonus: {
    referrerPoints: 0,
    refereePoints: 0,
    eligibilityMinSpendEur: 100,
    eligibilityWindowDays: 30,
  },
  ongoing: { rate: 0, durationMonths: 12, monthlyCapEur: 1000 },
  tierMultiplier: { free: 1, pro: 1 },
  adoptionRate: 0,
  avgReferralsPerReferrer: 0,
};

function getReferralConfig(inputs) {
  return inputs.referral ?? FALLBACK_REFERRAL;
}

/**
 * Estimate how many of this month's new users came via a referral.
 * Falls back on `inputs.channelMix.referral.weight` if defined; otherwise
 * derives from `referral.adoptionRate × avgReferralsPerReferrer`.
 *
 * @returns {number} new referral acquisitions this month
 */
export function estimateNewReferrals(inputs, totalNewThisMonth) {
  const cfg = getReferralConfig(inputs);
  if (!cfg.enabled) return 0;

  // Prefer explicit channel weight when present
  const channelShare = inputs.channelMix?.referral?.weight ?? 0;
  if (channelShare > 0) return totalNewThisMonth * channelShare;

  // Otherwise estimate from adoption × multiplier (rough heuristic — channel
  // mix is the canonical source if the user has set it).
  return totalNewThisMonth * cfg.adoptionRate * cfg.avgReferralsPerReferrer;
}

/**
 * Total referral cost for a single month, given:
 *   - newReferrals: new referral acquisitions this month
 *   - cohorts: array of { month, referrals, tierMix } for each prior month
 *              still in its 12-month payout window
 *   - tierSpend: { free, pro } EUR average monthly spend per active user
 *
 * Returns { activationCost, ongoingCost, totalCost }.
 *
 * Cohort entry shape: { month, referrals: { free, pro } }
 */
export function computeReferralCost(inputs, newReferrals, cohorts, currentMonth, tierSpend) {
  const cfg = getReferralConfig(inputs);
  if (!cfg.enabled || (newReferrals <= 0 && cohorts.length === 0)) {
    return { activationCost: 0, ongoingCost: 0, totalCost: 0 };
  }

  const blendedPt = blendedCostPerPoint(inputs.points);
  const pointFace = inputs.points.pointFaceValue;

  // Activation bonus — both referrer and referee on this month's new acquisitions
  const activationPoints =
    newReferrals * (cfg.activationBonus.referrerPoints + cfg.activationBonus.refereePoints);
  const activationCost = activationPoints * blendedPt;

  // Ongoing share — for every still-active cohort, sum the per-tier ongoing
  // share earned this month based on referee spend × tier multiplier × rate.
  let ongoingPoints = 0;
  for (const cohort of cohorts) {
    const monthsActive = currentMonth - cohort.month;
    if (monthsActive <= 0 || monthsActive > cfg.ongoing.durationMonths) continue;

    for (const tk of Object.keys(cohort.referrals)) {
      const cohortCount = cohort.referrals[tk] ?? 0;
      if (cohortCount <= 0) continue;
      const spend = tierSpend[tk] ?? 0;
      const eligibleSpend = Math.min(spend, cfg.ongoing.monthlyCapEur);
      const multiplier = cfg.tierMultiplier[tk] ?? 1;
      // Ongoing rate × eligible spend × tier multiplier — converted to points
      // by dividing by point face value (1% of €1000 spend = €10 face = 1000 pts at €0.01)
      const pointsEarned = (cfg.ongoing.rate * eligibleSpend * multiplier) / pointFace;
      ongoingPoints += cohortCount * pointsEarned;
    }
  }
  const ongoingCost = ongoingPoints * blendedPt;

  return {
    activationCost,
    ongoingCost,
    totalCost: activationCost + ongoingCost,
  };
}

/**
 * Allocate this month's new referrals across tiers using the same proConv
 * split that determines tier mix for organic acquisitions.
 *
 * @returns {{ free, pro }}
 */
export function allocateReferralsByTier(newReferrals, scenario) {
  const newPro = newReferrals * scenario.proConv;
  const newFree = Math.max(0, newReferrals - newPro);
  return { free: newFree, pro: newPro };
}
